import gettext from '@sdk/lang/gettext';
import { TIMECLOCK_NOTIFICATIONS } from '@sdk/common/constants';
import { ClockInGeoMode, EmailNotificationFrequency } from '@sdk/authentication/types';
import { PayrollPeriodType } from '@sdk/labor/types';
import { AlarmUnits } from '@sdk/shifts/types/shiftAlarmsType';

import { VisibilitySchedulesOptions } from './types/index';

export const DEFAULT_SETTINGS_PADDING = 20;

export const FIELD_WIDTH_74 = 74;
export const FIELD_WIDTH_100 = 100;
export const FIELD_WIDTH_150 = 150;
export const FIELD_WIDTH_205 = 205;
export const FIELD_WIDTH_420 = 420;

export const GOOGLE_CALENDAR_SYNC_URL =
  'https://support.getsling.com/schedules/how-to-synchronize-my-schedule-with-my-google-calendar';
export const DOWNLOAD_IOS_APP_URL =
  'https://itunes.apple.com/app/sling-stay-in-sync-your-team/id922995624?mt=8';
export const DOWNLOAD_ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=com.gangverk.sling';
export const SET_UP_TIME_CLOCK_KIOSK_URL =
  'https://support.getsling.com/time-clock-and-timesheets/how-do-i-set-up-a-time-clock-kiosk';
export const USE_SHIFT_ACCEPTANCE_URL =
  'https://support.getsling.com/en/articles/4319967-how-do-i-use-the-shift-acceptance-feature';

export const NOTIFICATIONS_NOTIFY_MODES = [
  { value: 0, label: gettext('Never') },
  { value: 1, label: gettext('Always when there is a new notification on my Dashboard') },
];

export const MESSAGES_NOTIFY_MODES = [
  { value: 1, label: gettext('Always when I receive a new message') },
  { value: 2, label: gettext('Only when I am mentioned or get a direct message') },
];

export const EMAIL_FREQUENCY_MODES: {
  value: EmailNotificationFrequency;
  label: string;
}[] = [
  { value: 0, label: gettext('Immediately') },
  { value: 15, label: gettext('Every 15 minutes') },
  { value: 60, label: gettext('Once an hour') },
];

export const NEWSFEED_NOTIFY_MODES = [
  { value: 1, label: gettext('Always when there is a new post to pages I follow') },
  { value: 2, label: gettext('Only when I am mentioned') },
];

export const TIME_FORMATS = [
  { value: 0, label: '13:40' },
  { value: 1, label: '1:40 PM' },
];

export const NAME_FORMATS = [
  { value: 0, label: 'John Appleseed' },
  { value: 1, label: 'Appleseed, John' },
];

export const UNIT_SYSTEMS = [
  { label: gettext('feet'), value: 1 },
  { label: gettext('meters'), value: 0 },
];

export const SHIFT_ALARM_OPTIONS_IN_MINUTES = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 20, label: '20' },
  { value: 30, label: '30' },
  { value: 45, label: '45' },
  { value: 60, label: '60' },
  { value: 120, label: '120' },
  { value: 180, label: '180' },
];

export const SHIFT_ALARM_OPTIONS_IN_HOURS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 6, label: '6' },
  { value: 9, label: '9' },
  { value: 12, label: '12' },
  { value: 18, label: '18' },
  { value: 24, label: '24' },
];

export const SHIFT_ALARMS_UNIT_OPTIONS = [
  { value: AlarmUnits.minutes, label: gettext('Minutes') },
  { value: AlarmUnits.hours, label: gettext('Hours') },
];

export const BREAK_OPTIONS = [
  { value: 1, label: gettext('Unpaid') },
  { value: 2, label: gettext('Paid') },
];

export const BREAK_DURATION_OPTIONS = [
  { value: 0, label: gettext('No break') },
  { value: 10, label: gettext('10 minutes') },
  { value: 15, label: gettext('15 minutes') },
  { value: 20, label: gettext('20 minutes') },
  { value: 30, label: gettext('30 minutes') },
  { value: 45, label: gettext('45 minutes') },
  { value: 60, label: gettext('60 minutes') },
];

export const CONVERSATION_TYPE = {
  NONE: 'NONE',
  PRIVATE_ONLY: 'PRIVATE_ONLY',
  ALL: 'ALL',
};

export const CONVERSATION_OPTIONS = [
  { label: gettext('Group and private conversations'), value: CONVERSATION_TYPE.ALL },
  { label: gettext('Only private conversations'), value: CONVERSATION_TYPE.PRIVATE_ONLY },
];

export const SINGLE_LOCATION_OTHER_SCHEDULES_OPTIONS = [
  {
    label: gettext('Employees can view full schedule for all positions'),
    value: VisibilitySchedulesOptions.accessPositionsEnabled,
  },
  {
    label: gettext('Employees can view schedule for their position'),
    value: VisibilitySchedulesOptions.accessPositionsDisabled,
  },
];

export const MULTI_LOCATION_OTHER_SCHEDULES_OPTIONS = [
  {
    label: gettext('Employees can view full schedule in all locations'),
    value: VisibilitySchedulesOptions.accessPositionsEnabledLocationsEnabled,
  },
  {
    label: gettext('Employees can view full schedule within their own location'),
    value: VisibilitySchedulesOptions.accessPositionsEnabledLocationsDisabled,
  },
  {
    label: gettext('Employees can view schedule for their position in all locations'),
    value: VisibilitySchedulesOptions.accessPositionsDisabledLocationsEnabled,
  },
  {
    label: gettext('Employees can view schedule for their position within their own location'),
    value: VisibilitySchedulesOptions.accessPositionsDisabledLocationsDisabled,
  },
];

export const LEAVE_REQUEST_BEFORE_DAYS_OPTIONS = [
  { label: gettext('1 day'), value: 1 },
  { label: gettext('2 days'), value: 2 },
  { label: gettext('3 days'), value: 3 },
  { label: gettext('4 days'), value: 4 },
  { label: gettext('5 days'), value: 5 },
  { label: gettext('6 days'), value: 6 },
  { label: gettext('7 days'), value: 7 },
  { label: gettext('14 days'), value: 14 },
  { label: gettext('21 days'), value: 21 },
  { label: gettext('28 days'), value: 28 },
];

export const CLOCK_IN_ROUNDING_OPTIONS = [
  { label: gettext('No rounding'), value: 0 },
  { label: gettext('5 minutes'), value: 5 },
  { label: gettext('10 minutes'), value: 10 },
  { label: gettext('15 minutes'), value: 15 },
];

export const CLOCK_NOTIFICATIONS_DEFAULT_VALUE = 15;
export const CLOCK_NOTIFICATIONS_MINUTES_OPTIONS = [
  { label: gettext('15 minutes'), value: 15 },
  { label: gettext('20 minutes'), value: 20 },
  { label: gettext('30 minutes'), value: 30 },
];

export const CLOCK_IN_NOTIFICATIONS_OPTIONS = [
  {
    label: gettext('Always when employees clock in or forget to clock in'),
    value: TIMECLOCK_NOTIFICATIONS.ALWAYS,
  },
  {
    label: gettext('Only when employees clock in late or forget to clock in'),
    value: TIMECLOCK_NOTIFICATIONS.ONLY_LATE,
  },
  { label: gettext('Never'), value: TIMECLOCK_NOTIFICATIONS.NEVER },
];

export const CLOCK_OUT_NOTIFICATIONS_OPTIONS = [
  {
    label: gettext('Always when employees clock out or forget to clock out'),
    value: TIMECLOCK_NOTIFICATIONS.ALWAYS,
  },
  {
    label: gettext('Only when employees clock out late or forget to clock out'),
    value: TIMECLOCK_NOTIFICATIONS.ONLY_LATE,
  },
  { label: gettext('Never'), value: TIMECLOCK_NOTIFICATIONS.NEVER },
];

export const TIME_CLOCK_GEOFENCING_MODES_OPTIONS = [
  {
    name: ClockInGeoMode.onlyClockIn,
    label: gettext('Clocking in'),
    value: false,
    disabled: false,
  },
  {
    name: ClockInGeoMode.onlyClockOut,
    label: gettext('Clocking out'),
    value: false,
    disabled: false,
  },
];

export const ANY_PROTOCOL_REGEX = /(^\w+:|^)\/\//;
export const SETTINGS_STAGING_BG_COLOR =
  'linear-gradient(90deg, rgba(255, 255, 255, 0), antiquewhite 5%, antiquewhite 95%, rgba(255, 255, 255, 0))';

export enum PayrollPeriodLabel {
  weekly = 'Weekly',
  biWeekly = 'Bi-weekly',
  semiMonthly = 'Semi-Monthly',
  monthly = 'Monthly',
}

export const PAYROLL_PERIOD_OPTIONS = [
  { value: PayrollPeriodType.weekly, label: PayrollPeriodLabel.weekly },
  { value: PayrollPeriodType.biWeekly, label: PayrollPeriodLabel.biWeekly },
  { value: PayrollPeriodType.semiMonthly, label: PayrollPeriodLabel.semiMonthly },
  { value: PayrollPeriodType.monthly, label: PayrollPeriodLabel.monthly },
];
